import * as React from "react"
import { Link } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  Bath,
  BedDouble,
  Building,
  Heart,
  LandPlot,
  MapPin,
  Maximize2,
  Navigation,
  Waves,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { toggleFavoriteFn } from "@/lib/app.functions"
import { formatPrice, titleCase } from "@/lib/constants"
import type { Property } from "@/lib/types"
import { cn } from "@/lib/utils"

export function FavoriteButton({
  propertyId,
  initialFavorite,
  signedIn,
  returnTo,
  compact = false,
}: {
  propertyId: number
  initialFavorite: boolean
  signedIn: boolean
  returnTo?: string
  compact?: boolean
}) {
  const toggle = useServerFn(toggleFavoriteFn)
  const [favorite, setFavorite] = React.useState(initialFavorite)
  const [pending, setPending] = React.useState(false)

  if (!signedIn) {
    return (
      <Button
        variant="outline"
        size={compact ? "icon-sm" : "default"}
        className="field-button bg-white"
        nativeButton={false}
        render={<Link to="/login" search={{ next: returnTo }} />}
        aria-label="Sign in to save property"
      >
        <Heart />
        {compact ? <span className="sr-only">Save property</span> : "Save"}
      </Button>
    )
  }

  const handleClick = async () => {
    setPending(true)
    try {
      const result = await toggle({ data: { id: propertyId } })
      setFavorite(result.favorite)
      toast.success(result.favorite ? "Property saved." : "Property removed.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to update.")
    } finally {
      setPending(false)
    }
  }

  return (
    <Button
      type="button"
      variant={favorite ? "default" : "outline"}
      size={compact ? "icon-sm" : "default"}
      className={cn(
        "field-button bg-white",
        favorite && "bg-primary text-white"
      )}
      disabled={pending}
      onClick={handleClick}
      aria-pressed={favorite}
      aria-label={favorite ? "Remove saved property" : "Save property"}
    >
      <Heart className={favorite ? "fill-current" : ""} />
      {compact ? (
        <span className="sr-only">{favorite ? "Saved" : "Save"}</span>
      ) : favorite ? (
        "Saved"
      ) : (
        "Save"
      )}
    </Button>
  )
}

export function PropertyCard({
  property,
  signedIn,
  className,
}: {
  property: Property
  signedIn: boolean
  className?: string
}) {
  const image = property.images.at(0)

  return (
    <article
      className={cn(
        "group grid min-w-0 border border-border bg-card md:grid-cols-[minmax(180px,0.78fr)_1fr]",
        className
      )}
    >
      <Link
        to="/property/$slug"
        params={{ slug: property.slug }}
        className="relative min-h-52 overflow-hidden bg-muted"
      >
        {image ? (
          <img
            src={image.url}
            alt={image.alt}
            className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.025]"
            loading="lazy"
          />
        ) : (
          <div className="grid size-full place-items-center">
            <Building className="size-10 text-muted-foreground" />
          </div>
        )}
        <Badge className="absolute right-3 bottom-3 rounded-none bg-white text-foreground">
          {titleCase(property.type)}
        </Badge>
      </Link>
      <div className="flex min-w-0 flex-col p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="field-label">{property.reference}</p>
          <FavoriteButton
            propertyId={property.id}
            initialFavorite={property.favorite}
            signedIn={signedIn}
            returnTo={`/property/${property.slug}`}
            compact
          />
        </div>
        <Link to="/property/$slug" params={{ slug: property.slug }}>
          <h3 className="mt-2 line-clamp-2 text-2xl leading-none uppercase group-hover:text-primary">
            {property.title}
          </h3>
        </Link>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="size-3.5" />
          {property.locality}, {property.region}
        </p>
        <p className="mt-4 font-heading text-2xl font-bold">
          {formatPrice(
            property.price,
            property.transaction,
            property.rentPeriod
          )}
        </p>
        <PropertyFacts property={property} compact />
      </div>
    </article>
  )
}

export function PropertyFacts({
  property,
  compact = false,
}: {
  property: Property
  compact?: boolean
}) {
  const facts = [
    property.bedrooms > 0
      ? { icon: BedDouble, value: property.bedrooms, label: "beds" }
      : null,
    property.bathrooms > 0
      ? { icon: Bath, value: property.bathrooms, label: "baths" }
      : null,
    property.floorArea > 0
      ? {
          icon: Maximize2,
          value: `${property.floorArea} m²`,
          label: "interior",
        }
      : null,
    property.landArea > 0
      ? { icon: LandPlot, value: `${property.landArea} m²`, label: "land" }
      : null,
  ].filter(Boolean) as Array<{
    icon: typeof BedDouble
    value: string | number
    label: string
  }>

  return (
    <dl
      className={cn(
        "mt-auto grid border-t border-border pt-4",
        compact ? "mt-4 grid-cols-2 gap-3" : "grid-cols-2 gap-4 sm:grid-cols-4"
      )}
    >
      {facts.slice(0, compact ? 4 : undefined).map((fact) => (
        <div key={fact.label} className="flex items-center gap-2">
          <dt className="contents">
            <fact.icon className="size-4 shrink-0 text-primary" />
            <span className="sr-only">{fact.label}</span>
          </dt>
          <dd className="text-xs font-semibold">
            {fact.value}{" "}
            {typeof fact.value === "number" ? (
              <span className="font-normal text-muted-foreground">
                {fact.label}
              </span>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function FeatureFlags({ property }: { property: Property }) {
  if (!property.nearCbd && !property.waterfront) {
    return null
  }
  return (
    <div className="flex flex-wrap gap-2">
      {property.nearCbd ? (
        <Badge variant="outline" className="rounded-none">
          <Navigation /> Near town center
        </Badge>
      ) : null}
      {property.waterfront ? (
        <Badge variant="outline" className="rounded-none">
          <Waves /> Waterfront
        </Badge>
      ) : null}
    </div>
  )
}
